<template>
  <div class="cause-verification-checkbox">
    <Checkbox
      :value="isCauseVerified"
      :title="title"
      :color="color"
      :icon="icon"
      stop-propagation
      @input="onVerifiedCheck"
    />

    <EditableLabelRadio
      ref="isRealOptions"
      :value="-1"
      :items="radioOptions"
      no-text
      @input="onSelectReal"
      @close="onSelectReal(2)"
    />
  </div>
</template>

<script>
/**
 * Component to display whether a cause is 'not verified', 'true' or 'false'
 */

// Components
import Checkbox from '@/components/checkbox.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import SocketMessages from '@/utils/classes/SocketMessages';
import { calert } from '@/utils/main';
import { EDIT_CAUSE } from '@/store/modules/problem/action-types';
import { checkboxCrossElement } from '@/assets/icons/';


export default {

  name: 'CauseVerificationCheckbox',

  components: {
    Checkbox,
    EditableLabelRadio,
  },

  mixins: [mixinSocketHandler],

  props: {
    cause: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      radioOptions: [
        { id: 0, label: this.$t('message.CAUSE_VERIFIED_NOT_REAL') },
        { id: 1, label: this.$t('message.CAUSE_VERIFIED_REAL') },
        { id: 2, label: this.$t('message.CAUSE_NOT_VERIFIED') },
      ],
    };
  },

  computed: {
    /** String: Title indicating whether this caused is verified and real or not */
    title() {
      if (!this.cause.isVerified) {
        return this.$t('message.CAUSE_NOT_VERIFIED');
      }
      return this.cause.isReal
        ? this.$t('message.CAUSE_VERIFIED_REAL')
        : this.$t('message.CAUSE_VERIFIED_NOT_REAL');
    },
    /** String: Color amongst available colors for a Checkbox component */
    color() {
      if (!this.cause.isVerified) {
        return 'gray';
      }
      return this.cause.isReal ? 'green' : 'orange';
    },
    /** String: Use a 'cross' icon if the cause is not real */
    icon() {
      if (this.cause.isVerified && !this.cause.isReal) {
        return checkboxCrossElement;
      }
      return null;
    },
    /** Boolean: Whether the checkbox should be checked */
    isCauseVerified() {
      return this.cause.isVerified === 1;
    },
  },

  methods: {
    /** Triggered when (un)checking the verified checkbox */
    onVerifiedCheck(isVerified) {
      if (!isVerified) {
        this.editCause({ isVerified: 0, isReal: 0 });
      } else {
        this.$refs.isRealOptions.edit();
      }
    },
    /** User selected a value in the radio options */
    onSelectReal(isReal) {
      if (isReal < 2) {
        this.editCause({ isVerified: 1, isReal });
      } else {
        if (this.cause.isVerified !== 0) {
          this.cause.isVerified = 0;
          this.editCause({ isVerified: 0, isReal: 0 });
        }
      }
    },
    /**
     * Handle edit events
     * @param  {Object} params - Params to send through XHR body (id and problemId will be overridden)
     * @param  {Number} causeId - If the cause to edit isn't this.cause, specify the causeId
     */
    editCause(editParams) {
      const params = { id: this.cause.id, problemId: this.cause.problem, ...editParams };
      this.$store.dispatch(`problem/${EDIT_CAUSE}`, params)
        .then(() => {
          this.sendSocketMessage({ socketCompany: 1, id: this.cause.problem, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch(this.handleError);
    },
    /** Handle XHR errors */
    handleError(msg) {
      if (msg === -2) {
        calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
      } else {
        calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
      }
    },
  },
};
</script>
